/**
 * Customer Email Service
 * Reusable service to fetch customer email by customerId, name, or phone
 */

import api from "../../../shared/api";
import { logWarn, logInfo } from "../../../shared/utils/logger";

/**
 * Fetch customer email by customerId, with fallback to name/phone search
 * @param {string} customerId - Customer identifier
 * @param {string} [customerName] - Customer name for fallback search
 * @param {string} [customerPhone] - Customer phone for fallback search
 * @returns {Promise<string|null>} Customer email or null if not found
 */
export const fetchCustomerEmail = async (customerId, customerName = null, customerPhone = null) => {
  if (!customerId) return null;
  
  // Method 1: Try direct lookup by customerId
  try {
    const response = await api.get(`/customers/${customerId}`);
    if (response?.data?.email) {
      logInfo("Customer email found by customerId", { customerId, email: response.data.email });
      return response.data.email;
    }
  } catch (error) {
    logWarn("Customer lookup by customerId failed, trying fallback", {
      customerId,
      error: error.message,
    });
  }

  // Method 2: Fallback to search by name if provided
  if (customerName) {
    try {
      const searchResponse = await api.get("/customers/search", {
        params: { q: customerName.trim() },
      });
      
      // Handle response structure: backend returns { success: true, data: [...] }
      // API client returns this directly, so searchResponse = { success: true, data: [...] }
      // But customerService.js uses response.data directly, so check both structures
      const customers = Array.isArray(searchResponse?.data) 
        ? searchResponse.data 
        : (searchResponse?.data?.data || []);
      
      if (Array.isArray(customers) && customers.length > 0) {
        // Find exact name match first
        const exactMatch = customers.find(
          (c) => c.name?.toLowerCase() === customerName.toLowerCase()
        );
        const customer = exactMatch || customers[0];
        
        if (customer?.email) {
          logInfo("Customer email found by name search", {
            customerId,
            customerName,
            email: customer.email,
          });
          return customer.email;
        }
      }
    } catch (error) {
      logWarn("Customer search by name failed", {
        customerName,
        error: error.message,
      });
    }
  }

  // Method 3: Fallback to search by phone if provided
  if (customerPhone) {
    try {
      const searchResponse = await api.get("/customers/search", {
        params: { q: customerPhone.trim() },
      });
      
      // Handle response structure: backend returns { success: true, data: [...] }
      // API client returns this directly, so searchResponse = { success: true, data: [...] }
      // But customerService.js uses response.data directly, so check both structures
      const customers = Array.isArray(searchResponse?.data) 
        ? searchResponse.data 
        : (searchResponse?.data?.data || []);
      
      if (Array.isArray(customers) && customers.length > 0) {
        const customer = customers[0];
        if (customer?.email) {
          logInfo("Customer email found by phone search", {
            customerId,
            customerPhone,
            email: customer.email,
          });
          return customer.email;
        }
      }
    } catch (error) {
      logWarn("Customer search by phone failed", {
        customerPhone,
        error: error.message,
      });
    }
  }

  logWarn("Customer email not found using any method", {
    customerId,
    customerName,
    customerPhone,
  });
  
  return null;
};

